import type { ComponentProps, ReactNode } from "react";
import { cn } from "~/lib/utils";

/** A chronological activity list. Indicators sit on one continuous, decorative rail. */
export function Timeline({ className, ...props }: ComponentProps<"ol">) {
  return <ol data-slot="timeline" className={cn("isolate", className)} {...props} />;
}
/**
 * One entry. `compact` is an event's one-liner: a small icon on the same rail,
 * the text beside it, and less room under it than a comment needs.
 */
export function TimelineItem({
  indicator,
  children,
  className,
  compact = false,
  ...props
}: ComponentProps<"li"> & { indicator: ReactNode; compact?: boolean }) {
  return (
    <li
      data-slot="timeline-item"
      data-compact={compact ? "" : undefined}
      className={cn(
        "relative flex min-w-0 gap-3 before:absolute before:bottom-0 before:left-4 before:w-px before:-translate-x-1/2 before:bg-border last:before:hidden",
        compact ? "before:top-6" : "before:top-8",
        className,
      )}
      {...props}
    >
      <span
        data-slot="timeline-indicator"
        className={cn(
          "relative z-10 flex w-8 shrink-0 items-center justify-center bg-background",
          compact ? "h-5" : "mt-0.5 h-8 rounded-full ring-4 ring-background",
        )}
      >
        {indicator}
      </span>
      <div className={cn("min-w-0 flex-1", compact ? "pb-4" : "pb-6")}>{children}</div>
    </li>
  );
}
